import { DOCUMENT, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SimpleCallback, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { WindowEventService } from '@frontend/sports/common/core/utils/window-event';
import { BehaviorSubject, Subject, auditTime, buffer, debounceTime, filter, map, merge, switchMap, takeUntil } from 'rxjs';

import { HandleType, IHandleStyles, IRangeStyles } from './model';
import { SliderEventService } from './slider-events.service';
import { SliderService } from './slider.service';

@Component({
    selector: 'ms-slider',
    templateUrl: `./slider.component.html`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SliderService, SliderEventService],
    imports: [NgStyle, NgClass, NgTemplateOutlet],
})
export class SliderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() disabled: boolean;
    @Input() range: boolean;
    @Input() steps: number[];
    @Input() value: number;
    @Input() tabindex = 0;
    @Input() maxValue: number;
    @Input() minIconTemplate?: TemplateRef<any>;
    @Input() maxIconTemplate?: TemplateRef<any>;
    @Input() stepId?: string;
    @Input() rangeVisible = true;
    @Input() highlightHandle = false;

    @Output() valueChange = new EventEmitter<number>();
    @Output() maxValueChange = new EventEmitter<number>();
    @Output() change = new EventEmitter<void>();

    @ViewChild('handle') handleRef: ElementRef;
    @ViewChild('pointSelector') pointSelector: ElementRef;

    @HostBinding('class.slider--disabled') get isDisabled(): boolean {
        return this.disabled;
    }

    handleType = HandleType;
    currentHandle: HandleType;
    minHandleStyles: IHandleStyles;
    maxHandleStyles: IHandleStyles;
    rangeStyles: IRangeStyles;
    private readonly selectedStepClass = 'step-selected';
    stepDivider = 4;
    active = false;

    private position$ = new Subject<number>();
    private dragging$ = new BehaviorSubject<boolean>(false);
    private maxStep: number;
    private barWidth: number;
    private sliderClientX: number;
    private skipClick = false;
    private clearCallback: SimpleCallback;
    private readonly destroyRef = inject(DestroyRef);
    private readonly _doc = inject(DOCUMENT);

    constructor(
        private el: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
        private sliderService: SliderService,
        private sliderEvents: SliderEventService,
        private timer: TimerService,
        private windowEventService: WindowEventService,
        private zone: NgZone,
    ) {}

    ngAfterViewInit(): void {
        this.resetHandles();
    }

    ngOnInit(): void {
        this.maxStep = this.steps.length - 1;
        this.sliderService.maxStep = this.maxStep;
        this.sliderService.range = this.range;
        this.sliderService.updateSteps(this.steps, this.value, this.maxValue);
        this.setStyles();

        const dragStart$ = this.dragging$.pipe(filter((d) => d));
        const dragEnd$ = this.dragging$.pipe(filter((d) => !d));

        this.zone.runOutsideAngular(() => {
            dragStart$
                .pipe(
                    switchMap(() =>
                        this.sliderEvents.mousemove.pipe(
                            map((event: MouseEvent) => event.clientX),
                            takeUntil(dragEnd$),
                        ),
                    ),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(this.position$);

            dragStart$
                .pipe(
                    switchMap(() => this.sliderEvents.mouseup.pipe(takeUntil(dragEnd$))),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(() => this.onEnd(true));

            this.position$
                .pipe(
                    filter(() => this.dragging$.getValue() && !this.disabled),
                    auditTime(20),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe((clientX) => this.handleNewPosition(clientX));
        });

        merge(this.valueChange, this.maxValueChange)
            .pipe(
                buffer(dragEnd$),
                filter((values) => values.length > 0),
                debounceTime(500),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.change.emit());

        this.windowEventService.resize
            .asObservable()
            .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.resetHandles();
            });
    }

    ngOnDestroy(): void {
        if (this.clearCallback) {
            this.clearCallback();
        }
    }

    ngOnChanges(changes: ISimpleChanges<SliderComponent>): void {
        if ((changes.value && !changes.value.firstChange) || (changes.maxValue && !changes.maxValue.firstChange)) {
            if (!changes.steps?.isFirstChange() && changes.steps?.currentValue !== changes.steps?.previousValue) {
                const stepDiff = changes.steps?.previousValue?.length! - changes.steps?.currentValue?.length!;
                if (stepDiff !== 0) {
                    this.maxStep = this.steps.length - 1;
                    this.sliderService.maxStep = this.maxStep;
                    this.sliderService.range = this.range;
                }
            }

            this.sliderService.updateSteps(this.steps, this.value, this.maxValue);
            this.setStyles();
        }
    }

    updateHandler(): void {
        if (!this.steps.some((x) => x === this.value)) {
            this.value = this.steps[0];
        }
        this.maxStep = this.steps.length - 1;
        this.sliderService.maxStep = this.maxStep;
        this.sliderService.range = this.range;
        this.sliderService.updateSteps(this.steps, this.value, this.maxValue);
        this.setStyles();
        this.valueChange.next(this.value);
    }

    @HostListener('click', ['$event'])
    click(event: MouseEvent): void {
        if (!this.wasRangeClicked(event) || this.skipClick || this.disabled) {
            return;
        }
        this.skipClick = false;

        const newStepPosition = this.calculateStepPosition(event.clientX);
        this.currentHandle = this.range ? this.sliderService.findClosestHandle(newStepPosition) : HandleType.MinHandle;

        this.update(newStepPosition, this.currentHandle);
        this.onEnd();
    }

    sliderExternalAction(handle: HandleType, value: number, _stepDivider: number): void {
        const byValue = value > 0 ? 1 : -1;
        const handleProps = handle === HandleType.MaxHandle ? this.sliderService.maxHandleProps : this.sliderService.minHandleProps;

        const newStep = handleProps.step + byValue;

        this.stepDivider = _stepDivider;
        this.update(newStep, handle);
        this.onEnd();
    }

    onHandleKeydown(event: KeyboardEvent, handle: HandleType): void {
        if (this.disabled) {
            return;
        }

        const incrementActions = ['ArrowRight', 'ArrowUp'];
        const decrementActions = ['ArrowLeft', 'ArrowDown'];

        if ([...decrementActions, ...incrementActions].indexOf(event.code) === -1) {
            return;
        }

        const byValue = incrementActions.indexOf(event.code) > -1 ? 1 : -1;

        const handleProps = handle === HandleType.MaxHandle ? this.sliderService.maxHandleProps : this.sliderService.minHandleProps;
        const newStep = handleProps.step + byValue;

        this.update(newStep, handle);
        this.onEnd();
    }

    onMouseDown(event: MouseEvent, handle: HandleType): void {
        this.onStart(event, handle);
    }

    onTouchStart(event: TouchEvent, handle: HandleType): void {
        this.onStart(event, handle);
    }

    onTouchMove(event: TouchEvent): void {
        const clientX = event.changedTouches[0].clientX;
        this.position$.next(clientX);
    }

    onTouchEnd(): void {
        this.onEnd();
    }

    resetHandles(): void {
        this.setDomData();
    }

    private handleNewPosition(clientX: number): void {
        const newStepPosition = this.calculateStepPosition(clientX);

        this.update(newStepPosition, this.currentHandle);
    }

    private update(newStep: number, handle: HandleType): void {
        const update = this.sliderService.handleStepChange(newStep, handle);
        if (!update) {
            return;
        }

        this.setStyles();
        if (handle === HandleType.MinHandle) {
            this.value = this.steps[this.sliderService.minHandleProps.step];
            this.valueChange.next(this.value);
        } else {
            this.maxValue = this.steps[this.sliderService.maxHandleProps.step];
            this.maxValueChange.next(this.maxValue);
        }
        this.changeDetectorRef.detectChanges();
    }

    private wasRangeClicked(event: MouseEvent): boolean {
        const target = event.target as HTMLElement;

        return target.matches(':not(.slider-handle)');
    }

    private calculateStepPosition(actionX: number): number {
        return ((this.maxStep + 1) * (actionX - this.sliderClientX)) / this.barWidth;
    }

    private onStart(event: MouseEvent | TouchEvent, handle: HandleType): void {
        event.stopPropagation();
        event.preventDefault();

        if (event instanceof MouseEvent) {
            (event.target as HTMLElement).focus();
        }
        this.active = this.highlightHandle ? true : false;
        this.dragging$.next(true);
        this.currentHandle = handle;
        this.changeDetectorRef.detectChanges();
    }

    private onEnd(skipNextClick: boolean = false): void {
        this.active = false;

        if (skipNextClick) {
            this.skipClick = true;
            this.timer.setTimeoutOutsideAngular(() => (this.skipClick = false), 100);
        }

        this.dragging$.next(false);
        this.changeDetectorRef.detectChanges();
    }

    private setDomData(): void {
        this.clearCallback = this.timer.setAnimationFrame(() => {
            this.sliderClientX = this.el.nativeElement.getBoundingClientRect().left;
            this.barWidth = this.el.nativeElement.offsetWidth;

            const handleOffset = ((this.handleRef.nativeElement.getBoundingClientRect().width / 2) * 100) / this.barWidth;
            this.sliderService.setHandleOffset(handleOffset);
            this.setStyles();
        });
    }

    private setStyles(): void {
        const isVisible = (left: number | undefined) => (isDefined(left) ? 'visible' : 'hidden');

        this.minHandleStyles = {
            left: this.getMinHandleleft(),
            visibility: isVisible(this.sliderService.minHandleProps.left),
        };

        this.maxHandleStyles = {
            left: `${this.sliderService.maxHandleProps.left}%`,
            visibility: isVisible(this.sliderService.maxHandleProps.left),
        };

        if (this.range) {
            this.rangeStyles = {
                left: `${this.sliderService.minHandleProps.left}%`,
                width: `${this.sliderService.maxHandleProps.left! - this.sliderService.minHandleProps.left!}%`,
            };
        } else {
            this.rangeStyles = {
                left: `0`,
                width: `${this.sliderService.minHandleProps.left}%`,
            };
        }
        this.changeDetectorRef.detectChanges();
    }

    private getMinHandleleft(): string {
        if (this.stepId) {
            const teaser_point_id = `${this.stepId}${this.value}`;
            const elem = this._doc.getElementById(teaser_point_id);
            if (elem) {
                this.resetClass();
                if (!elem.classList.contains(this.selectedStepClass)) {
                    elem.classList.add(this.selectedStepClass);
                }

                const selectedStep = this._doc.getElementsByClassName(this.selectedStepClass);
                let selectedStepWidth = 0;
                if (selectedStep.length > 0) {
                    selectedStepWidth = selectedStep[0].clientWidth;
                }

                return `${elem.offsetLeft + selectedStepWidth / 2}px`;
            }
        }

        return `${this.sliderService.minHandleProps.left}%`;
    }

    private resetClass(): void {
        const element: HTMLElement = this.pointSelector.nativeElement;
        if (element) {
            Array.from(element.children)?.forEach((e) => e?.classList?.remove(this.selectedStepClass));
        }
    }
}
